import { useState, useCallback, useEffect } from 'react';
import {
  addManualUploadFiles,
  removeManualUploadFile,
  updateManualUploadFileStatus,
  type UploadedFile,
} from '../../../../redux/globalSlices/cashApplicationSlice';
import {
  processUploadedFileThunk,
  fetchEmailIngestionThunk,
  enableEmailIngestionThunk,
  disableEmailIngestionThunk,
  fetchRemittanceEmailsNowThunk,
  fetchBankFeedThunk,
  createBankFeedLinkTokenThunk,
  connectBankFeedThunk,
  disconnectBankFeedThunk,
  syncBankFeedNowThunk,
} from '../../../../redux/services/cashApplicationService';
import { usePlaidLink, type PlaidLinkOnSuccess } from 'react-plaid-link';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import {
  Bank,
  EnvelopeSimple,
  LockKey,
  UploadSimple,
  CheckCircle,
  XCircle,
  ArrowRight,
  Lightning,
  ShieldCheck,
  Clock,
  File,
  Trash,
  Copy,
  ArrowClockwise,
  Hourglass,
} from 'phosphor-react';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { DillButton, DillModal } from '../../../general/Dill';
import {
  CashAppLockedBanner,
  lockedCardClass,
  useCashAppEnabled,
} from '../components/CashAppLocked';
import { formatDateTime } from '../utils';

export const CashApplicationDataIntegrations = () => {
  const dispatch = useAppDispatch();
  const { emailIngestion, bankFeed, bankFeedLinkToken, manualUploadFiles } = useAppSelector(
    (state) => state.cashApplication
  );
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;

  // Every control on this page connects a source that only matters once ingestion runs, so all
  // of them are disabled together rather than one card at a time.
  const cashAppEnabled = useCashAppEnabled();
  const locked = !cashAppEnabled;

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  // A manual check runs IMAP fetch plus AI extraction, so it can take a while with
  // nothing on screen. Shared by the card and the modal so both spin together.
  const [checkingEmails, setCheckingEmails] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // Only a Connect Bank click may open Plaid Link. The link token stays in Redux after use,
  // so opening on its presence alone reopens Link on every remount without a click.
  const [pendingPlaidOpen, setPendingPlaidOpen] = useState(false);

  useEffect(() => {
    if (supplierId) {
      dispatch(fetchEmailIngestionThunk({ supplierId }));
      dispatch(fetchBankFeedThunk({ supplierId }));
    }
  }, [dispatch, supplierId]);

  // ── Plaid / Bank Feed ─────────────────────────────────────────
  const bankLinked = !!bankFeed?.linked && bankFeed.enabled !== false;
  const bankLabel = bankLinked
    ? [bankFeed?.institutionName, bankFeed?.mask ? `****${bankFeed.mask}` : null]
        .filter(Boolean)
        .join(' ') || 'Linked'
    : 'Not Connected';

  const handlePlaidSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken, metadata) => {
      if (!supplierId) return;
      const result = await dispatch(
        connectBankFeedThunk({
          supplierId,
          publicToken,
          accountId: metadata.accounts?.[0]?.id,
        })
      );
      if (result.meta.requestStatus === 'fulfilled') {
        openDillToast({
          message: 'Bank account linked. Remittances will now wait for settlement.',
          type: 'SUCCESS',
        });
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to link bank account.',
          type: 'ERROR',
        });
      }
    },
    [dispatch, supplierId]
  );

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: bankFeedLinkToken,
    onSuccess: handlePlaidSuccess,
  });

  // Link needs the token before it can open, so fetching it and opening are two steps: the
  // click below requests the token and arms pendingPlaidOpen, and this effect opens Link once
  // the hook is ready. Disarmed immediately so a later openPlaidLink identity change — which
  // happens when Link is cancelled — cannot reopen it.
  useEffect(() => {
    if (pendingPlaidOpen && bankFeedLinkToken && plaidReady) {
      setPendingPlaidOpen(false);
      openPlaidLink();
    }
  }, [pendingPlaidOpen, bankFeedLinkToken, plaidReady, openPlaidLink]);

  const handleConnectBank = async () => {
    if (!supplierId) return;
    const result = await dispatch(createBankFeedLinkTokenThunk({ supplierId }));
    if (result.meta.requestStatus !== 'fulfilled') {
      openDillToast({
        message: (result.payload as string) ?? 'Failed to start bank linking.',
        type: 'ERROR',
      });
      return;
    }
    // Armed only once the fresh token is in the store, so the effect can never fire against
    // the previous — possibly expired — token.
    setPendingPlaidOpen(true);
  };

  const handleDisconnectBank = async () => {
    if (!supplierId) return;
    const result = await dispatch(disconnectBankFeedThunk({ supplierId }));
    if (result.meta.requestStatus === 'fulfilled') {
      openDillToast({ message: 'Bank feed disconnected.', type: 'SUCCESS' });
    } else {
      openDillToast({
        message: (result.payload as string) ?? 'Failed to disconnect bank feed.',
        type: 'ERROR',
      });
    }
  };

  const handleSyncBankNow = async () => {
    if (!supplierId) return;
    const result = await dispatch(syncBankFeedNowThunk({ supplierId }));
    if (result.meta.requestStatus === 'fulfilled') {
      const summary = result.payload as {
        creditsSeen: number;
        settled: number;
        ambiguous: number;
      };
      openDillToast({
        message:
          `Checked ${summary.creditsSeen} deposit(s), confirmed ${summary.settled}` +
          (summary.ambiguous > 0 ? `, ${summary.ambiguous} need review` : ''),
        type: 'SUCCESS',
      });
      dispatch(fetchBankFeedThunk({ supplierId }));
    } else {
      openDillToast({
        message: (result.payload as string) ?? 'Failed to sync bank feed.',
        type: 'ERROR',
      });
    }
  };

  // ── Remittance Email ──────────────────────────────────────────
  // Three states: not set up, enabled but no mail yet (pending), and active
  // once the first forwarded message has been routed to us.
  const emailEnabled = emailIngestion?.enabled ?? false;
  const emailActive = emailEnabled && !!emailIngestion?.lastReceivedAt;
  const forwardingAddress = emailIngestion?.forwardingAddress ?? null;

  const emailStatusLabel = !emailEnabled ? 'Not Set Up' : emailActive ? 'Active' : 'Pending';

  const handleEnableEmail = async () => {
    if (!supplierId) return;
    const result = await dispatch(enableEmailIngestionThunk({ supplierId }));
    if (result.meta.requestStatus === 'fulfilled') {
      openDillToast({
        message: 'Forwarding address created. Set up forwarding to start receiving remittances.',
        type: 'SUCCESS',
      });
    } else {
      openDillToast({
        message: (result.payload as string) ?? 'Failed to set up remittance email.',
        type: 'ERROR',
      });
    }
  };

  const handleDisableEmail = async () => {
    if (!supplierId) return;
    const result = await dispatch(disableEmailIngestionThunk({ supplierId }));
    setEmailModalOpen(false);
    if (result.meta.requestStatus === 'fulfilled') {
      openDillToast({ message: 'Remittance email disconnected.', type: 'SUCCESS' });
    } else {
      openDillToast({
        message: (result.payload as string) ?? 'Failed to disconnect remittance email.',
        type: 'ERROR',
      });
    }
  };

  const handleCheckEmailsNow = async () => {
    if (!supplierId || checkingEmails) return;
    setCheckingEmails(true);
    try {
      const result = await dispatch(fetchRemittanceEmailsNowThunk({ supplierId, hours: 24 * 5 }));
      if (result.meta.requestStatus === 'fulfilled') {
        const { processed, skipped, failed } = result.payload as {
          processed: number;
          skipped: number;
          failed: number;
        };
        if (processed > 0) {
          openDillToast({
            message: `${processed} remittance email(s) processed. Check the review queue.`,
            type: 'SUCCESS',
          });
        } else {
          // The check itself succeeded, there was just nothing to process — openDillToast has
          // no informational variant, and ERROR would be wrong here.
          openDillToast({
            message: `No new remittances found.${skipped ? ` ${skipped} message(s) skipped.` : ''}${
              failed ? ` ${failed} failed.` : ''
            }`,
            type: 'SUCCESS',
          });
        }
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to check for new remittance emails.',
          type: 'ERROR',
        });
      }
    } finally {
      setCheckingEmails(false);
    }
  };

  const handleCopyAddress = async () => {
    if (!forwardingAddress) return;
    try {
      await navigator.clipboard.writeText(forwardingAddress);
      openDillToast({ message: 'Forwarding address copied.', type: 'SUCCESS' });
    } catch (error) {
      openDillToast({
        message: 'Could not copy the address. Please copy it manually.',
        type: 'ERROR',
      });
    }
  };

  // ── Manual Upload ─────────────────────────────────────────────
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!supplierId) {
        openDillToast({ message: 'Supplier not found. Please log in again.', type: 'ERROR' });
        return;
      }

      const uploads: UploadedFile[] = acceptedFiles.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        uploadedAt: new Date().toISOString(),
        status: 'processing' as const,
      }));
      dispatch(addManualUploadFiles(uploads));
      setUploadModalOpen(false);
      openDillToast({
        message: `Uploading ${acceptedFiles.length} file(s)...`,
        type: 'SUCCESS',
      });

      for (const file of acceptedFiles) {
        try {
          // Upload to Firebase Storage
          const blobUrl = URL.createObjectURL(file);
          const timestamp = Date.now();
          const fileUrl = await firebaseLargeFileUploadHelper({
            blobUrl,
            storagePath: 'cashApplication/uploads',
            fileNameWithExtension: `${timestamp}_${file.name}`,
          });
          URL.revokeObjectURL(blobUrl);

          if (!fileUrl) {
            dispatch(updateManualUploadFileStatus({ name: file.name, status: 'error' }));
            openDillToast({ message: `Failed to upload ${file.name}.`, type: 'ERROR' });
            continue;
          }

          // Send to backend for AI processing
          const result = await dispatch(
            processUploadedFileThunk({
              supplierId,
              fileUrl,
              mimeType: file.type || 'application/octet-stream',
              fileName: file.name,
            })
          ).unwrap();

          const recordCount = result.data.remittanceRecords.length;
          openDillToast({
            message: `${file.name}: ${recordCount} payment(s) detected and matched.`,
            type: 'SUCCESS',
          });
        } catch (error) {
          dispatch(updateManualUploadFileStatus({ name: file.name, status: 'error' }));
          openDillToast({ message: `Failed to process ${file.name}.`, type: 'ERROR' });
        }
      }
    },
    [dispatch, supplierId]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: true,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col h-full px-6 py-4 gap-6 overflow-auto">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-gray-900">Data Integrations</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Connect payment sources to feed remittance data into your cash application pipeline.
        </p>
      </div>

      {locked && <CashAppLockedBanner />}

      {/* Pipeline banner */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
            <Lightning size={18} weight="fill" className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">Ingest</span>
        </div>
        <ArrowRight size={16} className="text-gray-400" />
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg">
            <ShieldCheck size={18} weight="fill" className="text-purple-600" />
          </div>
          <span className="font-medium text-gray-900">Match</span>
        </div>
        <ArrowRight size={16} className="text-gray-400" />
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center justify-center w-8 h-8 bg-emerald-100 rounded-lg">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" />
          </div>
          <span className="font-medium text-gray-900">Post</span>
        </div>
        <div className="ml-auto text-xs text-gray-500">
          3-stage pipeline: Exact {'→'} Fuzzy {'→'} AI Vector Search
        </div>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ── Bank Feed (Plaid) ─────────────────────────────── */}
        <div
          aria-disabled={locked}
          className={
            'flex flex-col p-5 bg-white rounded-xl border border-gray-200 shadow-sm' +
            lockedCardClass(locked)
          }>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600">
              <Bank size={28} weight="duotone" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Bank Feed (Plaid)</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                {bankLinked ? (
                  <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                ) : (
                  <XCircle size={14} weight="fill" className="text-gray-400" />
                )}
                <span
                  className={
                    'text-xs font-medium ' + (bankLinked ? 'text-emerald-600' : 'text-gray-400')
                  }>
                  {bankLabel}
                </span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-3 flex-1">
            Connect your bank account so Dill can confirm a payment actually arrived before posting
            it. While a bank is linked, confidently matched remittances wait for the matching
            deposit instead of posting on the advice alone.
          </p>
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg mb-3">
            {bankFeed?.lastError ? (
              <XCircle size={14} weight="fill" className="text-red-500 flex-shrink-0" />
            ) : (
              <Clock size={14} className="text-gray-400 flex-shrink-0" />
            )}
            <p className={'text-xs ' + (bankFeed?.lastError ? 'text-red-600' : 'text-gray-500')}>
              {bankFeed?.lastError
                ? bankFeed.lastError
                : !bankLinked
                ? 'Link your bank account via Plaid to get started.'
                : bankFeed?.lastSyncedAt
                ? `Last synced ${formatDateTime(bankFeed.lastSyncedAt)}`
                : 'Linked — waiting for the first sync.'}
            </p>
          </div>
          {bankLinked ? (
            <div className="flex items-center gap-2">
              <DillButton
                onClick={handleSyncBankNow}
                label="Check Deposits"
                isDisabled={locked}
                variant="secondary"
                className="flex-1"
                height={36}
              />
              <DillButton
                onClick={handleDisconnectBank}
                label="Disconnect"
                isDisabled={locked}
                variant="ghost"
                height={36}
              />
            </div>
          ) : (
            <DillButton
              onClick={handleConnectBank}
              label="Connect Bank"
              isDisabled={locked}
              variant="primary"
              className="w-full"
              height={36}
            />
          )}
        </div>

        {/* ── Remittance Email ──────────────────────────────── */}
        <div
          aria-disabled={locked}
          className={
            'flex flex-col p-5 bg-white rounded-xl border border-gray-200 shadow-sm' +
            lockedCardClass(locked)
          }>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-purple-50 text-purple-600">
              <EnvelopeSimple size={28} weight="duotone" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Remittance Email</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                {emailActive ? (
                  <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                ) : emailEnabled ? (
                  <Hourglass size={14} weight="fill" className="text-amber-500" />
                ) : (
                  <XCircle size={14} weight="fill" className="text-gray-400" />
                )}
                <span
                  className={
                    'text-xs font-medium ' +
                    (emailActive
                      ? 'text-emerald-600'
                      : emailEnabled
                      ? 'text-amber-600'
                      : 'text-gray-400')
                  }>
                  {emailStatusLabel}
                </span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-3 flex-1">
            Forward remittance advice emails to your Dill address. Dill extracts payment details
            from attachments or the email body and matches them against open invoices.
          </p>
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg mb-3">
            <Clock size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500 truncate">
              {emailActive && emailIngestion?.lastReceivedAt
                ? `Last remittance received ${formatDateTime(emailIngestion.lastReceivedAt)}`
                : emailEnabled
                ? 'Waiting for the first forwarded email.'
                : 'Get a forwarding address to start receiving remittance emails.'}
            </p>
          </div>
          {emailEnabled ? (
            <div className="flex items-center gap-2">
              <DillButton
                onClick={handleCheckEmailsNow}
                label={checkingEmails ? 'Checking...' : 'Check Now'}
                variant="secondary"
                icon={<ArrowClockwise size={14} />}
                isLoading={checkingEmails}
                isDisabled={locked}
                className="flex-1"
                height={36}
              />
              <DillButton
                onClick={() => setEmailModalOpen(true)}
                label="Manage"
                isDisabled={locked}
                variant="ghost"
                height={36}
              />
            </div>
          ) : (
            <DillButton
              onClick={() => setEmailModalOpen(true)}
              label="Set Up Email"
              isDisabled={locked}
              variant="primary"
              className="w-full"
              height={36}
            />
          )}
        </div>

        {/* ── Lockbox ──────────────────────────────────────── */}
        <div
          aria-disabled={locked}
          className={
            'flex flex-col p-5 bg-white rounded-xl border border-gray-200 shadow-sm' +
            lockedCardClass(locked)
          }>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600">
              <LockKey size={28} weight="duotone" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Lockbox Integration</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <XCircle size={14} weight="fill" className="text-gray-400" />
                <span className="text-xs font-medium text-gray-400">Not Set Up</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-3 flex-1">
            Import lockbox files from your bank (BAI2, CSV). Payments are parsed and matched against
            open invoices automatically. For companies that use bank lockbox services for check
            processing.
          </p>
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg mb-3">
            <Clock size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500">
              Set up lockbox integration to start importing bank lockbox files.
            </p>
          </div>
          <DillButton
            onClick={() => {}}
            label="Set Up Lockbox"
            isDisabled={locked}
            variant="primary"
            className="w-full"
            height={36}
          />
        </div>

        {/* ── Manual Upload ─────────────────────────────────── */}
        <div
          aria-disabled={locked}
          className={
            'flex flex-col p-5 bg-white rounded-xl border border-gray-200 shadow-sm' +
            lockedCardClass(locked)
          }>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 text-gray-600">
              <UploadSimple size={28} weight="duotone" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Manual Upload</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                <span className="text-xs font-medium text-emerald-600">Always Available</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-3 flex-1">
            Upload check images, remittance PDFs, or spreadsheets. Dill extracts payment details
            using OCR and AI document understanding.
          </p>

          {/* Recent uploads */}
          {manualUploadFiles.length > 0 && (
            <div className="mb-3 space-y-1">
              {manualUploadFiles.slice(0, 3).map((file) => (
                <div
                  key={file.name + file.uploadedAt}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <File size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-700 truncate flex-1">{file.name}</span>
                  <span className="text-[10px] text-gray-400">{formatFileSize(file.size)}</span>
                  <span
                    className={
                      'text-[10px] font-medium ' +
                      (file.status === 'processing'
                        ? 'text-amber-600'
                        : file.status === 'matched'
                        ? 'text-emerald-600'
                        : 'text-gray-400')
                    }>
                    {file.status === 'processing' ? 'Processing' : 'Processed'}
                  </span>
                  <DillButton
                    onClick={() => dispatch(removeManualUploadFile(file.name))}
                    variant="icon-red"
                    isDisabled={locked}
                    icon={<Trash size={12} />}
                  />
                </div>
              ))}
              {manualUploadFiles.length > 3 && (
                <p className="text-[10px] text-gray-400 text-center">
                  +{manualUploadFiles.length - 3} more file(s)
                </p>
              )}
            </div>
          )}

          <DillButton
            onClick={() => setUploadModalOpen(true)}
            label="Upload Files"
            isDisabled={locked}
            variant="primary"
            icon={<UploadSimple size={14} />}
            className="w-full"
            height={36}
          />
        </div>
      </div>

      {/* ── Email Setup Modal ───────────────────────────────── */}
      <DillModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        title="Remittance Email Forwarding"
        subtitle={
          emailEnabled
            ? 'Forward remittance advice to the address below and Dill will process it automatically.'
            : 'Dill gives you a dedicated address. Forward remittance advice to it and we handle the rest — no mailbox password needed.'
        }
        maxWidth="520px"
        footer={
          emailEnabled ? (
            <>
              <DillButton onClick={handleDisableEmail} label="Disconnect" variant="ghost" />
              <DillButton onClick={() => setEmailModalOpen(false)} label="Done" variant="primary" />
            </>
          ) : (
            <>
              <DillButton onClick={() => setEmailModalOpen(false)} label="Cancel" variant="ghost" />
              <DillButton onClick={handleEnableEmail} label="Get My Address" variant="primary" />
            </>
          )
        }>
        {emailEnabled && forwardingAddress ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Your forwarding address
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-900 truncate">
                {forwardingAddress}
              </code>
              <DillButton
                onClick={handleCopyAddress}
                variant="icon-blue"
                icon={<Copy size={16} />}
              />
            </div>

            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Set up forwarding</p>
              <ol className="text-xs text-gray-500 space-y-1.5 list-decimal list-inside">
                <li>
                  Open the mailbox where you receive remittance advice (for example
                  ar@yourcompany.com).
                </li>
                <li>
                  <strong>Gmail:</strong> Settings → Forwarding and POP/IMAP → Add a forwarding
                  address. <strong>Outlook:</strong> Settings → Mail → Forwarding.
                </li>
                <li>Paste the address above and confirm the verification request.</li>
                <li>
                  Forward broadly — every payer's remittance, not just a filtered subset. Dill
                  ignores anything that isn't a remittance.
                </li>
              </ol>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-700">
                    {emailActive ? 'Receiving remittances' : 'No email received yet'}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {emailIngestion?.lastFetchedAt
                      ? `Last checked ${formatDateTime(emailIngestion.lastFetchedAt)}`
                      : 'Dill checks for new mail every 30 minutes.'}
                  </p>
                </div>
                <DillButton
                  onClick={handleCheckEmailsNow}
                  label={checkingEmails ? 'Checking...' : 'Check Now'}
                  variant="secondary"
                  icon={<ArrowClockwise size={14} />}
                  isLoading={checkingEmails}
                />
              </div>
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700">
                Remittances land in the <strong>1-Click Review</strong> queue when Dill is confident
                about the match, and in <strong>Exceptions</strong> when it needs your input.
              </p>
            </div>
          </div>
        ) : (
          <div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700">
                We'll generate a unique address for your account. Anything forwarded to it is parsed
                for payment details and matched against your open invoices — attachments and
                plain-text emails both work.
              </p>
            </div>
            <ol className="text-xs text-gray-500 space-y-1.5 list-decimal list-inside mt-3">
              <li>Generate your address below.</li>
              <li>Add it as a forwarding address in your remittance mailbox.</li>
              <li>Confirm the verification request we receive on your behalf.</li>
            </ol>
          </div>
        )}
      </DillModal>

      {/* ── File Upload Modal ───────────────────────────────── */}
      <DillModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Payment Documents"
        subtitle="Upload check images, remittance PDFs, or spreadsheets for processing."
        maxWidth="520px"
        contentClassName="flex flex-col gap-4 px-5 py-4"
        footer={
          <>
            <DillButton onClick={() => setUploadModalOpen(false)} label="Cancel" variant="ghost" />
          </>
        }>
        <div
          {...getRootProps()}
          className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 hover:bg-blue-50/50 hover:border-blue-300 transition-colors cursor-pointer">
          <input {...getInputProps()} />
          <div className="flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full">
            <UploadSimple size={28} className="text-blue-600" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700">
              Drag & drop files here, or click to browse
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Supports PDF, JPG, PNG, HEIC, CSV, and Excel files
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400 text-center">
          Files are processed using OCR and AI document understanding. Multiple files can be
          uploaded at once for batch processing.
        </p>
      </DillModal>
    </div>
  );
};
